/** Display helpers for 360° match breakdown (aligned with server skillMatch.js). */

export const TIER_LABELS = {
  strong: {
    label: "Strong match",
    className:
      "bg-[hsl(var(--ds-success)/0.12)] text-[hsl(var(--ds-success))] border border-[hsl(var(--ds-success)/0.25)]",
  },
  good: {
    label: "Good fit",
    className:
      "bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))] border border-[hsl(var(--ds-primary)/0.25)]",
  },
  partial: {
    label: "Partial fit",
    className:
      "bg-[hsl(var(--ds-warning)/0.15)] text-[hsl(var(--ds-warning-fg))] border border-[hsl(var(--ds-warning)/0.35)]",
  },
  weak: {
    label: "Weak",
    className:
      "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))] border border-[hsl(var(--ds-border))]",
  },
  not_relevant: {
    label: "Not relevant",
    className:
      "bg-[hsl(var(--ds-danger)/0.12)] text-[hsl(var(--ds-danger))] border border-[hsl(var(--ds-danger)/0.25)]",
  },
};

export const FACTOR_STATUS_ICON = {
  pass: '✓',
  partial: '~',
  fail: '✗',
  unknown: '?',
  na: '—'
};

export function tierMeta(tier) {
  return TIER_LABELS[tier] || TIER_LABELS.weak;
}

export function overallPercent(row) {
  return row.overallMatchPercent ?? row.matchPercent ?? 0;
}

export function skillsPercent(row) {
  return row.skillsMatchPercent ?? row.skillsOnlyPercent ?? 0;
}

export function coreCompetencyPercent(row) {
  if (row?.coreCompetencyPercent != null) return row.coreCompetencyPercent;
  const factor = row?.matchFactors?.find(f => f.key === 'coreCompetencies');
  if (factor?.percent != null) return factor.percent;
  const mustCore = row?.totalMustCore ?? 0;
  if (mustCore > 0) {
    return Math.round(((row?.mustMatched || []).length / mustCore) * 100);
  }
  return null;
}

export function defaultShowInList(row, { showAll }) {
  if (showAll) return true;
  const coreMust = row?.totalMustCore ?? 0;
  const corePct = coreCompetencyPercent(row);
  if (coreMust > 0 && (corePct == null || corePct < 25)) return false;
  const tier = row.relevanceTier || 'weak';
  if (tier === 'not_relevant') return false;
  if (tier === 'weak' && overallPercent(row) < 25) return false;
  return overallPercent(row) >= 25 || tier === 'partial' || tier === 'good' || tier === 'strong';
}

export const MATCH_WEIGHT_SUMMARY =
  'Core competencies 50% · Technical skills 15% · Education 10% · Experience 10% · Location 6% · Package 5% · Gender 1% · Age 1% · Working mode 2% (only factors set on the job are counted).';

const FACTOR_ORDER = [
  'coreCompetencies',
  'technicalSkills',
  'skills',
  'education',
  'experience',
  'location',
  'package',
  'gender',
  'age',
  'workingMode'
];

/** All factors in display order (includes not-scored factors). */
export function orderedMatchFactors(matchRow) {
  const list = matchRow?.matchFactors || [];
  if (!list.length) return synthesizeMatchFactors(matchRow);
  const byKey = Object.fromEntries(list.map(f => [f.key, f]));
  return FACTOR_ORDER.map(key => byKey[key]).filter(Boolean);
}

function boolLabel(v) {
  if (v === true) return 'Match';
  if (v === false) return 'Gap';
  return '—';
}

/** Fallback when API row lacks matchFactors (older payloads). */
function synthesizeMatchFactors(row) {
  if (!row) return [];
  const weights = {
    coreCompetencies: 50,
    technicalSkills: 15,
    skills: 50,
    education: 10,
    experience: 10,
    location: 6,
    package: 5,
    gender: 1,
    age: 1,
    workingMode: 2
  };
  const mk = (key, label, applies, percent, match, summary, detail, distanceKm = null) => ({
    key,
    label,
    weight: weights[key] || 0,
    applies: Boolean(applies),
    percent: percent != null ? Math.round(percent) : match ? 100 : 0,
    match: applies ? match : null,
    status:
      !applies ? 'na' : match === null ? 'unknown' : percent >= 80 || match ? 'pass' : percent >= 50 ? 'partial' : 'fail',
    summary: summary || '—',
    detail: detail || '',
    distanceKm: Number.isFinite(distanceKm) ? Math.round(distanceKm) : null,
    pointsEarned: applies && percent != null ? Math.round(((weights[key] || 0) * percent) / 100) : 0,
    pointsMax: applies ? weights[key] || 0 : 0
  });

  const skillPct = skillsPercent(row);
  const coreMustTotal = row.totalMustCore ?? 0;
  const coreNiceTotal = row.totalNiceCore ?? 0;
  const techMustTotal = row.totalMustTechnical ?? 0;
  const techNiceTotal = row.totalNiceTechnical ?? 0;
  const hasCore = coreMustTotal + coreNiceTotal > 0;
  const hasTech = techMustTotal + techNiceTotal > 0;

  return [
    mk(
      'coreCompetencies',
      'Core competencies',
      hasCore,
      hasCore && coreMustTotal > 0
        ? Math.round(((row.mustMatched || []).length / coreMustTotal) * 100)
        : skillPct,
      (row.mustMatched || []).length > 0 || !hasCore,
      hasCore
        ? `${(row.mustMatched || []).length}/${coreMustTotal} must · ${(row.niceMatched || []).length}/${coreNiceTotal} nice`
        : 'No core competencies on job',
      (row.mustMissing || []).length
        ? `Missing must-have: ${row.mustMissing.join(', ')}`
        : hasCore
          ? 'All must-have core competencies covered'
          : ''
    ),
    mk(
      'technicalSkills',
      'Technical skills',
      hasTech,
      hasTech && techMustTotal > 0
        ? Math.round(((row.techMustMatched || []).length / techMustTotal) * 100)
        : null,
      (row.techMustMatched || []).length > 0 || !hasTech,
      hasTech
        ? `${(row.techMustMatched || []).length}/${techMustTotal} must · ${(row.techNiceMatched || []).length}/${techNiceTotal} nice`
        : 'No technical skills on job',
      (row.techMustMissing || []).length
        ? `Missing must-have: ${row.techMustMissing.join(', ')}`
        : hasTech
          ? 'All must-have technical skills covered'
          : ''
    ),
    mk(
      'education',
      'Education / qualification',
      Boolean(row.requiredQualification || row.requiredQualifications?.length),
      row.qualificationMatch ? 100 : 0,
      row.qualificationMatch,
      row.requiredQualification || '—',
      row.candidateQualification || 'Not on profile'
    ),
    mk(
      'experience',
      'Experience (years)',
      row.candidateExperienceYears != null || row.experienceMatch != null,
      row.experienceMatch ? 100 : 0,
      row.experienceMatch,
      'Job experience range',
      row.candidateExperienceYears != null ? `Candidate: ${row.candidateExperienceYears} yrs` : 'Not on profile'
    ),
    mk(
      'location',
      'Location',
      row.locationMatch != null || row.locationDetail,
      row.locationMatch ? 100 : row.locationDistanceKm != null ? (row.locationDistanceKm <= 50 ? 82 : row.locationDistanceKm <= 120 ? 50 : 0) : 0,
      row.locationMatch,
      row.matchFactors?.find(f => f.key === 'location')?.summary || 'Job location',
      row.locationDetail ||
        (row.locationDistanceKm != null ? `~${Math.round(row.locationDistanceKm)} km from job` : '—'),
      row.locationDistanceKm
    ),
    mk('package', 'Package / CTC', row.packageMatch != null, row.packageMatch ? 100 : 0, row.packageMatch, '—', '—'),
    mk('gender', 'Gender preference', row.genderMatch != null, row.genderMatch ? 100 : 0, row.genderMatch, '—', '—'),
    mk('age', 'Age group', row.ageGroupMatch != null, row.ageGroupMatch ? 100 : 0, row.ageGroupMatch, '—', '—'),
    mk(
      'workingMode',
      'Working mode (WFH / WFO / Hybrid)',
      row.workingModeMatch != null,
      row.workingModeMatch ? 100 : 0,
      row.workingModeMatch,
      '—',
      '—'
    )
  ];
}

export function activeFactorPoints(matchRow) {
  const factors = orderedMatchFactors(matchRow).filter(f => f.applies);
  const earned = factors.reduce((s, f) => s + (f.pointsEarned || 0), 0);
  const max = factors.reduce((s, f) => s + (f.pointsMax || 0), 0);
  return { earned, max, count: factors.length };
}

export { boolLabel };
